import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

interface Colors {
  primary: string;
  secondary: string;
  accent: string;
  button: string;
  text: string;
  background: string;
  header: string;
  footer: string;
}

const defaultColors: Colors = {
  primary: '#C9A84C',
  secondary: '#1a1a1a',
  accent: '#D4AF37',
  button: '#C9A84C',
  text: '#F5F0E8',
  background: '#0d0d0d',
  header: '#0d0d0d',
  footer: '#260a23',
};

const colorFields: { key: keyof Colors; label: string; desc: string }[] = [
  { key: 'primary', label: 'Cor Principal', desc: 'Usada em destaques e elementos principais' },
  { key: 'secondary', label: 'Cor Secundária', desc: 'Fundos de cards e seções secundárias' },
  { key: 'accent', label: 'Cor de Destaque', desc: 'Hover, bordas ativas e ênfases' },
  { key: 'button', label: 'Cor dos Botões', desc: 'Fundo dos botões primários' },
  { key: 'text', label: 'Cor dos Textos', desc: 'Texto principal do site' },
  { key: 'background', label: 'Cor de Fundo', desc: 'Fundo geral das páginas' },
  { key: 'header', label: 'Cor do Header', desc: 'Fundo da barra de navegação' },
  { key: 'footer', label: 'Cor do Footer', desc: 'Fundo do rodapé' },
];

export default function AdminColorsPage() {
  const [colors, setColors] = useState<Colors>(defaultColors);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    loadColors();
  }, []);

  const loadColors = async () => {
    const { data } = await supabase
      .from('site_settings')
      .select('value_json')
      .eq('key', 'colors')
      .maybeSingle();
    if (data?.value_json) {
      setColors({ ...defaultColors, ...(data.value_json as Colors) });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const { data: existing } = await supabase
      .from('site_settings')
      .select('id')
      .eq('key', 'colors')
      .maybeSingle();

    if (existing) {
      await supabase.from('site_settings').update({ value_json: colors, updated_at: new Date().toISOString() }).eq('key', 'colors');
    } else {
      await supabase.from('site_settings').insert({ key: 'colors', value_json: colors });
    }
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-800">Cores e Identidade Visual</h1>
          <p className="text-gray-400 text-sm mt-0.5">Personalize a paleta de cores do site</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#C9A84C] hover:bg-[#D4AF37] text-black text-sm font-semibold px-5 py-2.5 rounded-lg whitespace-nowrap cursor-pointer transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {saving ? <><i className="ri-loader-4-line animate-spin"></i> Salvando...</> : saved ? <><i className="ri-check-line"></i> Salvo!</> : <><i className="ri-save-line"></i> Salvar</>}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Color pickers */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 p-5">
          <h2 className="text-sm font-semibold text-gray-700 border-b border-gray-100 pb-3 mb-4">Paleta de Cores</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {colorFields.map(({ key, label, desc }) => (
              <div key={key} className="flex items-center gap-4 p-3 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors">
                <div className="relative shrink-0">
                  <div
                    className="w-12 h-12 rounded-lg border-2 border-white cursor-pointer"
                    style={{ background: colors[key] }}
                  />
                  <input
                    type="color"
                    value={colors[key]}
                    onChange={e => setColors({ ...colors, [key]: e.target.value })}
                    className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                  />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-700">{label}</p>
                  <p className="text-xs text-gray-400 truncate">{desc}</p>
                  <p className="text-xs font-mono text-gray-500 mt-0.5">{colors[key]}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Preview */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h2 className="text-sm font-semibold text-gray-700 border-b border-gray-100 pb-3 mb-4">Pré-visualização</h2>
          <div className="rounded-lg overflow-hidden border border-gray-100">
            {/* Header preview */}
            <div className="px-4 py-3 flex items-center justify-between" style={{ background: colors.header }}>
              <span className="text-xs font-semibold" style={{ color: colors.primary }}>Ma Gádeni</span>
              <div className="flex gap-2">
                <span className="text-[10px]" style={{ color: colors.text + '80' }}>Loja</span>
                <span className="text-[10px]" style={{ color: colors.text + '80' }}>Whiskies</span>
              </div>
            </div>
            {/* Body preview */}
            <div className="px-4 py-5" style={{ background: colors.background }}>
              <p className="text-[10px] tracking-widest uppercase mb-1" style={{ color: colors.primary }}>Destaque</p>
              <p className="text-sm font-light mb-2" style={{ color: colors.text, fontFamily: "'Cormorant Garamond', serif" }}>Título da Seção</p>
              <p className="text-[10px] mb-3" style={{ color: colors.text + '60' }}>Texto descritivo da seção com informações relevantes sobre o produto.</p>
              <button className="text-[10px] font-bold px-3 py-1.5 rounded" style={{ background: colors.button, color: '#000' }}>
                Ver Mais
              </button>
            </div>
            {/* Footer preview */}
            <div className="px-4 py-3" style={{ background: colors.footer }}>
              <p className="text-[10px]" style={{ color: colors.text + '40' }}>© 2024 Ma Gádeni</p>
            </div>
          </div>

          <div className="mt-4 space-y-2">
            {colorFields.map(({ key, label }) => (
              <div key={key} className="flex items-center gap-2">
                <div className="w-4 h-4 rounded shrink-0" style={{ background: colors[key] }} />
                <span className="text-xs text-gray-500 flex-1">{label}</span>
                <span className="text-xs font-mono text-gray-400">{colors[key]}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
